import { Action, ThunkDispatch } from '@reduxjs/toolkit';

import { DELAYED_SPINNER_DEFAULT_TIMEOUT } from '@paragon/client-tools';

import { getAppMonitor } from '../monitor';
import { spinnerMessagesSlice, StateWithSpinnerMessages } from '../store/spinnerMessagesSlice';
import { AsyncThunkActionReturn, ThunkActionReturn, ThunkActionVoid } from '../thunkActions';

type SpinnerOperation<T> = Promise<T> | (() => T | Promise<T>);

let id = -1;

export const addSpinnerMessage =
  (message: string): ThunkActionReturn<number> =>
  (dispatch) => {
    id += 1;

    const timerId = getAppMonitor()?.beginTimedOperation('Task Spinner Displayed', { message });
    const metadata = { timerId };

    dispatch(spinnerMessagesSlice.actions.spinnerMessageAdded({ id, message, metadata }));

    return id;
  };

export const removeSpinnerMessage =
  (id: number): ThunkActionVoid =>
  (dispatch, getState) => {
    const spinner = getState().spinnerMessages[id];
    if (spinner && spinner.metadata?.timerId) {
      const timerId = spinner.metadata.timerId as number;

      // using `setTimeout` to ensure monitoring can't block or break the spinner
      setTimeout(() => {
        getAppMonitor()?.concludeTimedOperation(timerId);
      });
    }

    dispatch(spinnerMessagesSlice.actions.spinnerMessageRemoved(id));
  };

export const runDelayedSpinner =
  (message: string, timeout = DELAYED_SPINNER_DEFAULT_TIMEOUT): ThunkActionReturn<() => void> =>
  (dispatch) => {
    let spinnerId: number | null = null;

    const timeoutHandle = setTimeout(() => {
      spinnerId = dispatch(addSpinnerMessage(message));
    }, timeout);

    return () => {
      if (spinnerId !== null) {
        dispatch(removeSpinnerMessage(spinnerId));
      } else {
        clearTimeout(timeoutHandle);
      }
    };
  };

export function runWithSpinner<T>(message: string, operation: SpinnerOperation<T>): AsyncThunkActionReturn<T> {
  return async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage(message));

    try {
      const runningPromise = typeof operation === 'function' ? Promise.resolve(operation()) : operation;
      return await runningPromise;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function createSpinnerRunner<S extends StateWithSpinnerMessages, A extends Action>(
  dispatch: ThunkDispatch<S, void, A>,
) {
  return <T>(message: string, operation: SpinnerOperation<T>): Promise<T> =>
    dispatch(runWithSpinner(message, operation));
}
